import advanceReportingConstants from "@/constants/advanceReportingConstants"
import { advReportingNoCacheApi } from "@/store/modules/axiousWithCache"

const getScheduledReportsInitialState = () => {
  return {
    search: "",
    sort: null,
    scheduledReports: [],
    pagination: {
      activePage: 1,
      total: 0,
      pages: 0,
      per_page: { label: "25 / Page", value: 25 }
    }
  }
}

const state = {
  schoolList: [],
  isAdvanceReportingPageLoading: false,
  isDateDependentChartDataLoading: false,
  error: null,
  selectedSchools: [],
  kpiData: null,
  selectedDateRange: null,
  dateDependentChartData: null,
  dateIndependentChartData: null,
  userPreferences: null,
  chartSettings: [],
  frontendIdVsBackendIdMap: null,
  scheduledReports: getScheduledReportsInitialState(),
  revealedNamesMatrix: {} // Maps FE Chart ID to boolean
}

const getters = {
  schoolList: (state) => state?.schoolList || [],
  revealedNamesMatrix: (state) => state?.revealedNamesMatrix || {},
  areAllSchoolsSelected: (state) => {
    const allSchools = state?.schoolList || []
    const selectedSchools = state?.selectedSchools || []
    if (allSchools.length <= 1) {
      return false
    }
    return selectedSchools.length === allSchools.length
  },
  kpiData: (state) => state?.kpiData,
  selectedDateRange: (state) => state?.selectedDateRange,
  dateIndependentChartData: (state) => state?.dateIndependentChartData,
  dateDependentChartData: (state) => state?.dateDependentChartData,
  frontendIdVsBackendIdMap: (state) => state?.frontendIdVsBackendIdMap || null,
  chartSettings: (state) => state?.chartSettings || [],
  configurableChartList: (state) => {
    if (!state?.frontendIdVsBackendIdMap) {
      return []
    }
    const defaultAvailableCharts = []
    advanceReportingConstants.CONFIGURABLE_CHART_IDS.forEach(
      (feChartId, index) => {
        const beChartId = state?.frontendIdVsBackendIdMap?.[feChartId] || null
        if (beChartId) {
          defaultAvailableCharts.push({
            id: beChartId,
            feChartId,
            index: index + 1,
            status: true,
            name: advanceReportingConstants.CHART_ID_VS_TITLE[feChartId]
          })
        }
      }
    )
    return defaultAvailableCharts
  },
  nonConfigurableChartList: (state) => {
    if (!state?.frontendIdVsBackendIdMap) {
      return []
    }
    return advanceReportingConstants.NON_CONFIGURABLE_CHART_IDS.map(
      (feChartId) => {
        return {
          id: state?.frontendIdVsBackendIdMap?.[feChartId] || null,
          feChartId,
          index: 1,
          status: true,
          name: advanceReportingConstants.CHART_ID_VS_TITLE[feChartId]
        }
      }
    )
  },
  allChartList: (_state, getters) => {
    return [
      ...getters.nonConfigurableChartList,
      ...getters.configurableChartList
    ]
  },
  scheduledReports: (state) => state?.scheduledReports
}

const validateAndNormalizeKeyMatrix = (keyMatrix) => {
  if (!keyMatrix || typeof keyMatrix !== "object") {
    return {}
  }
  return keyMatrix
}

const validateAndNormalizeSchoolYearPassBreakdown = (
  schoolYearPassBreakdownObj
) => {
  if (
    !schoolYearPassBreakdownObj ||
    typeof schoolYearPassBreakdownObj !== "object"
  ) {
    return null
  }

  const entries = Object.entries(schoolYearPassBreakdownObj)
  if (entries.length === 0) {
    return []
  }

  const schoolYearPassBreakdownArray = entries
    .map(([key, item]) => {
      if (!item || typeof item !== "object") {
        return null
      }

      const name =
        item?.name ||
        advanceReportingConstants.PASS_TYPE_KEY_TO_NAME[key] ||
        key ||
        ""

      if (!name) {
        return null
      }

      const value = item?.value
      if (value === null || value === undefined) {
        return {
          name: String(name),
          value: 0
        }
      }

      const numValue = Number(parseInt(value || 0))
      return {
        name: String(name),
        value: isNaN(numValue) ? 0 : numValue
      }
    })
    .filter((item) => item && item?.name)
    .sort((a, b) => {
      const orderA =
        advanceReportingConstants.PASS_TYPE_ORDER.indexOf(a?.name) !== -1
          ? advanceReportingConstants.PASS_TYPE_ORDER.indexOf(a?.name)
          : Infinity
      const orderB =
        advanceReportingConstants.PASS_TYPE_ORDER.indexOf(b?.name) !== -1
          ? advanceReportingConstants.PASS_TYPE_ORDER.indexOf(b?.name)
          : Infinity
      return orderA - orderB
    })

  return schoolYearPassBreakdownArray.length > 0
    ? schoolYearPassBreakdownArray
    : null
}

const validateAndNormalizeLineChartData = (chartData) => {
  if (!chartData || typeof chartData !== "object") {
    return null
  }

  const current = chartData?.current || null
  const previous = chartData?.previous || null
  const categories = chartData?.categories || null

  if (
    !Array.isArray(current) ||
    !Array.isArray(previous) ||
    !Array.isArray(categories)
  ) {
    return null
  }

  if (current.length === 0) {
    return {
      current: [],
      previous: [],
      categories: []
    }
  }

  const normalizedCurrent = current.map((value) => {
    if (value === null || value === undefined) {
      return 0
    }
    const numValue = Number(value)
    return isNaN(numValue) ? 0 : numValue
  })

  const normalizedPrevious = previous.map((value) => {
    if (value === null || value === undefined) {
      return 0
    }
    const numValue = Number(value)
    return isNaN(numValue) ? 0 : numValue
  })

  const normalizedCategories = categories.map((category) => {
    if (category === null || category === undefined) {
      return ""
    }
    return String(category)
  })

  return {
    current: normalizedCurrent,
    previous: normalizedPrevious,
    categories: normalizedCategories
  }
}

const validateAndNormalizePassBreakdown = (passBreakdown) => {
  if (!passBreakdown || !Array.isArray(passBreakdown)) {
    return null
  }

  if (passBreakdown.length === 0) {
    return []
  }

  const isValid = passBreakdown.every(
    (item) =>
      item &&
      typeof item === "object" &&
      typeof item?.name === "string" &&
      typeof item?.value === "number"
  )

  if (!isValid) {
    return null
  }

  const sortedPassBreakdown = [...passBreakdown].sort((a, b) => {
    const orderA =
      advanceReportingConstants.PASS_TYPE_ORDER.indexOf(a?.name) !== -1
        ? advanceReportingConstants.PASS_TYPE_ORDER.indexOf(a?.name)
        : Infinity
    const orderB =
      advanceReportingConstants.PASS_TYPE_ORDER.indexOf(b?.name) !== -1
        ? advanceReportingConstants.PASS_TYPE_ORDER.indexOf(b?.name)
        : Infinity
    return orderA - orderB
  })

  return sortedPassBreakdown
}

const validateAndNormalizeTotalPassesBySchools = (totalPassesBySchools) => {
  if (
    !totalPassesBySchools ||
    typeof totalPassesBySchools !== "object" ||
    !totalPassesBySchools?.detailed ||
    !Array.isArray(totalPassesBySchools.detailed)
  ) {
    return null
  }

  const detailed = totalPassesBySchools.detailed

  if (detailed.length === 0) {
    return null
  }

  const isValidDetailed = detailed.every(
    (item) =>
      item &&
      typeof item === "object" &&
      typeof item?.name === "string" &&
      item?.name &&
      (typeof item?.student === "number" ||
        typeof item?.student === "string") &&
      (typeof item?.kiosk === "number" || typeof item?.kiosk === "string") &&
      (typeof item?.teacher === "number" ||
        typeof item?.teacher === "string") &&
      (typeof item?.appointment === "number" ||
        typeof item?.appointment === "string") &&
      (typeof item?.slp === "number" || typeof item?.slp === "string")
  )

  if (!isValidDetailed) {
    return null
  }

  const categories = detailed.map((item) => {
    if (item?.name === null || item?.name === undefined) {
      return ""
    }
    return String(item.name)
  })

  const values = detailed.map((item) => {
    const student = Number(item?.student || 0)
    const kiosk = Number(item?.kiosk || 0)
    const teacher = Number(item?.teacher || 0)
    const appointment = Number(item?.appointment || 0)
    const slp = Number(item?.slp || 0)
    return student + kiosk + teacher + appointment + slp
  })

  return {
    categories,
    values,
    seriesName:
      totalPassesBySchools?.seriesName || "Total Passes Granted By Schools",
    detailed
  }
}

const validateAndNormalizeChartData = (chartData) => {
  if (!chartData || typeof chartData !== "object") {
    return null
  }

  const categories = chartData?.categories || null
  const values = chartData?.values || null
  const seriesName = chartData?.seriesName || null
  const detailed = chartData?.detailed || null
  const schools = chartData?.schools || null

  if (!Array.isArray(categories) || !Array.isArray(values)) {
    return null
  }

  if (typeof seriesName !== "string") {
    return null
  }

  if (categories.length !== values.length) {
    return null
  }

  if (detailed !== null && !Array.isArray(detailed)) {
    return null
  }

  if (categories.length === 0) {
    return {
      categories: [],
      values: [],
      seriesName: String(seriesName),
      detailed: detailed || null,
      schools: schools || null
    }
  }

  const normalizedCategories = categories.map((category) => {
    if (category === null || category === undefined) {
      return ""
    }
    return String(category)
  })

  const normalizedValues = values.map((value) => {
    if (value === null || value === undefined) {
      return 0
    }
    const numValue = Number(value)
    return isNaN(numValue) ? 0 : numValue
  })

  return {
    categories: normalizedCategories,
    values: normalizedValues,
    seriesName: String(seriesName),
    detailed: detailed || null,
    schools: schools || null
  }
}

const actions = {
  fetchSchoolList(context, options = {}) {
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .get(`/schools`, { cancelToken: options?.cancelToken })
        .then((response) => {
          if (Array.isArray(response?.data?.data)) {
            context.commit("setSchoolList", response?.data?.data || [])
            resolve(response?.data?.data)
            return
          }
          reject(new Error("Something went wrong while fetching school list!"))
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  fetchAdvanceReportingPageData(context, options = {}) {
    context.commit("setIsAdvanceReportingPageLoading", true)
    return Promise.all([
      context.dispatch("fetchKpiAndDateDependentChartData", {
        cancelToken: options?.cancelToken
      }),
      context.dispatch("fetchDateDependentChartData", {
        cancelToken: options?.cancelToken
      }),
      context.dispatch("fetchDateDependentLineChartData", {
        cancelToken: options?.cancelToken
      })
    ])
      .catch((error) => {
        context.commit("setError", error)
        throw error
      })
      .finally(() => {
        context.commit("setIsAdvanceReportingPageLoading", false)
      })
  },
  fetchKpiAndDateDependentChartData(context, options = {}) {
    const params = {}
    const schoolIds = (
      context?.state?.selectedSchools?.map((school) => school?.id) || []
    ).join(",")

    const areAllSchoolsSelected =
      context?.getters?.areAllSchoolsSelected || false

    if (!areAllSchoolsSelected) {
      params.school_ids = schoolIds
    }

    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .get(`/kpi`, {
          params,
          cancelToken: options?.cancelToken
        })
        .then((response) => {
          if (response?.data?.success) {
            const responseData = response?.data?.data || {}

            const keyMatrix = responseData?.keyMatrix || null
            const validatedKeyMatrix = validateAndNormalizeKeyMatrix(keyMatrix)
            context.commit("setKpiData", validatedKeyMatrix)

            const schoolYearPassBreakdownObj =
              responseData?.schoolYearPassBreakdown || null
            const validatedSchoolYearPassBreakdown =
              validateAndNormalizeSchoolYearPassBreakdown(
                schoolYearPassBreakdownObj
              )

            const schoolYearPassTrend =
              responseData?.schoolYearPassTrend || null
            const validatedSchoolYearPassTrend =
              validateAndNormalizeLineChartData(schoolYearPassTrend)

            const existingDateIndependentChartData =
              context?.state?.dateIndependentChartData || {}
            const dateIndependentChartData = {
              ...existingDateIndependentChartData
            }

            if (validatedSchoolYearPassBreakdown) {
              dateIndependentChartData[
                advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_BREAKDOWN
              ] = validatedSchoolYearPassBreakdown
            }

            if (validatedSchoolYearPassTrend) {
              dateIndependentChartData[
                advanceReportingConstants.DEFAULT_CHART_IDS.SCHOOL_YEAR_PASS_TREND
              ] = validatedSchoolYearPassTrend
            }

            context.commit(
              "setDateIndependentChartData",
              dateIndependentChartData
            )

            resolve(responseData)
            return
          }

          reject(
            new Error(
              response?.data?.message ||
                "Something went wrong while fetching advanced reporting data!"
            )
          )
        })
        .catch((error) => {
          context.commit("setError", error)
          reject(error)
        })
    })
  },
  fetchDateDependentChartData(
    context,
    { skipLoadingState = false, cancelToken } = {}
  ) {
    const params = {}
    const selectedDateRange = context?.state?.selectedDateRange || null

    const schoolIds =
      context?.state?.selectedSchools?.map((school) => school?.id)?.join(",") ||
      ""
    const areAllSchoolsSelected =
      context?.getters?.areAllSchoolsSelected || false

    if (!areAllSchoolsSelected) {
      params.school_ids = schoolIds
    }

    params.date_range = selectedDateRange?.type || null
    params.start_date = selectedDateRange?.startDate || null
    params.end_date = selectedDateRange?.endDate || null

    return new Promise((resolve, reject) => {
      if (!skipLoadingState) {
        context.commit("setIsDateDependentChartDataLoading", true)
      }
      advReportingNoCacheApi
        .get(`/chart`, { params, cancelToken })
        .then((response) => {
          if (response?.data?.success) {
            const responseData = response?.data?.data || {}
            const validatedChartData = {}

            const passBreakdown = responseData?.passBreakdown || null
            const validatedPassBreakdown =
              validateAndNormalizePassBreakdown(passBreakdown)
            if (validatedPassBreakdown) {
              validatedChartData.passBreakdown = validatedPassBreakdown
            }

            const totalPassesBySchools =
              responseData?.totalPassesBySchools || null
            const validatedTotalPassesBySchools =
              validateAndNormalizeTotalPassesBySchools(totalPassesBySchools)
            if (validatedTotalPassesBySchools) {
              validatedChartData.totalPassesBySchools =
                validatedTotalPassesBySchools
            }

            const mostPassesTaken = responseData?.mostPassesTaken || null
            const validatedMostPassesTaken =
              validateAndNormalizeChartData(mostPassesTaken)
            if (validatedMostPassesTaken) {
              validatedChartData.mostPassesTaken = validatedMostPassesTaken
            }

            const mostPassesGranted = responseData?.mostPassesGranted || null
            const validatedMostPassesGranted =
              validateAndNormalizeChartData(mostPassesGranted)
            if (validatedMostPassesGranted) {
              validatedChartData.mostPassesGranted = validatedMostPassesGranted
            }

            const passBreakdownByGraduationYear =
              responseData?.passBreakdownByGraduationYear || null
            const validatedPassBreakdownByGraduationYear =
              validateAndNormalizeChartData(passBreakdownByGraduationYear)
            if (validatedPassBreakdownByGraduationYear) {
              validatedChartData.passBreakdownByGraduationYear =
                validatedPassBreakdownByGraduationYear
            }

            const averagePassDuration =
              responseData?.averagePassDuration || null
            const validatedAveragePassDuration =
              validateAndNormalizeChartData(averagePassDuration)
            if (validatedAveragePassDuration) {
              validatedChartData.averagePassDuration =
                validatedAveragePassDuration
            }

            const mostFrequentedLocations =
              responseData?.mostFrequentedLocations || null
            const validatedMostFrequentedLocations =
              validateAndNormalizeChartData(mostFrequentedLocations)
            if (validatedMostFrequentedLocations) {
              validatedChartData.mostFrequentedLocations =
                validatedMostFrequentedLocations
            }

            const contactControl = responseData?.contactControl || null
            const validatedContactControl =
              validateAndNormalizeChartData(contactControl)
            if (validatedContactControl) {
              validatedChartData.contactControl = validatedContactControl
            }

            const existingChartData =
              context?.state?.dateDependentChartData || {}
            const mergedChartData = {
              ...existingChartData,
              ...validatedChartData
            }

            resolve(responseData)
            context.commit("setDateDependentChartData", mergedChartData)
            return
          }

          reject(
            new Error(
              response?.data?.message ||
                "Something went wrong while fetching chart data!"
            )
          )
        })
        .catch((error) => {
          context.commit("setError", error)
          reject(error)
        })
        .finally(() => {
          if (!skipLoadingState) {
            context.commit("setIsDateDependentChartDataLoading", false)
          }
        })
    })
  },
  fetchDateDependentLineChartData(
    context,
    { skipLoadingState = false, cancelToken } = {}
  ) {
    const params = {}
    const selectedDateRange = context?.state?.selectedDateRange || null

    const schoolIds =
      context?.state?.selectedSchools?.map((school) => school?.id)?.join(",") ||
      ""
    const areAllSchoolsSelected =
      context?.getters?.areAllSchoolsSelected || false

    if (!areAllSchoolsSelected) {
      params.school_ids = schoolIds
    }

    params.date_range = selectedDateRange?.type || null
    params.start_date = selectedDateRange?.startDate || null
    params.end_date = selectedDateRange?.endDate || null

    return new Promise((resolve, reject) => {
      if (!skipLoadingState) {
        context.commit("setIsDateDependentChartDataLoading", true)
      }
      advReportingNoCacheApi
        .get(`/line-chart`, { params, cancelToken })
        .then((response) => {
          if (response?.data?.success) {
            const responseData = response?.data?.data || {}
            const validatedLineChartData = {}

            const passTrend = responseData?.passTrend || null
            const validatedPassTrend =
              validateAndNormalizeLineChartData(passTrend)
            if (validatedPassTrend) {
              validatedLineChartData.passTrend = validatedPassTrend
            }

            const tardyInfractions = responseData?.tardyInfractions || null
            const validatedTardyInfractions =
              validateAndNormalizeLineChartData(tardyInfractions)
            if (validatedTardyInfractions) {
              validatedLineChartData.tardyInfractions =
                validatedTardyInfractions
            }

            const visitorsTrend = responseData?.visitorsTrend || null
            const validatedVisitorsTrend =
              validateAndNormalizeLineChartData(visitorsTrend)
            if (validatedVisitorsTrend) {
              validatedLineChartData.visitorsTrend = validatedVisitorsTrend
            }

            const zeroGranters = responseData?.zeroGranters || null
            const validatedZeroGranters =
              validateAndNormalizeLineChartData(zeroGranters)
            if (validatedZeroGranters) {
              validatedLineChartData.zeroGranters = validatedZeroGranters
            }

            const existingChartData =
              context?.state?.dateDependentChartData || {}
            const mergedChartData = {
              ...existingChartData,
              ...validatedLineChartData
            }

            resolve(responseData)
            context.commit("setDateDependentChartData", mergedChartData)
            return
          }

          reject(
            new Error(
              response?.data?.message ||
                "Something went wrong while fetching line chart data!"
            )
          )
        })
        .catch((error) => {
          context.commit("setError", error)
          reject(error)
        })
        .finally(() => {
          if (!skipLoadingState) {
            context.commit("setIsDateDependentChartDataLoading", false)
          }
        })
    })
  },
  fetchChartSettings(context, options = {}) {
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .get(`/chart-settings`, { cancelToken: options?.cancelToken })
        .then((response) => {
          const chartSettings =
            response?.data?.chartSettings
              ?.map((setting) => {
                return {
                  id: setting.chart_id,
                  feChartId:
                    advanceReportingConstants.CHART_BE_NAME_VS_ID[
                      setting?.chart?.name
                    ],
                  index: setting.index,
                  status: setting.status,
                  name: advanceReportingConstants.CHART_ID_VS_TITLE[
                    advanceReportingConstants.CHART_BE_NAME_VS_ID[
                      setting?.chart?.name
                    ]
                  ]
                }
              })
              ?.sort((a, b) => Number(a.index) - Number(b.index)) || []
          const transformedResponse = {
            data: {
              ...response?.data,
              chartSettings
            }
          }
          resolve(transformedResponse || null)
          context.commit(
            "setChartSettings",
            transformedResponse?.data?.chartSettings || []
          )
          context.commit(
            "setFrontendIdVsBackendIdMap",
            transformedResponse?.data?.charts || []
          )
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  updateChartSettings(_context, chartSettings) {
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .post(`/chart-settings`, chartSettings)
        .then((response) => {
          resolve(response?.data || null)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  fetchScheduledReports(context) {
    const state = context.state.scheduledReports
    const params = {
      page: state.pagination.activePage,
      per_page: state.pagination.per_page.value,
      sort: "created_at:desc" // Sort by created at in descending order
    }

    if (state.search && state.search.trim()) {
      params.search = state.search
    }

    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .get(`/reports`, { params })
        .then((response) => {
          if (response?.data) {
            const { total = 0, data = [], current_page = 1 } = response.data
            context.commit("setScheduledReportsData", {
              total,
              data,
              current_page
            })
          }
          resolve(response?.data || null)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  downloadOrEmailReport(context, payload) {
    const selectedDateRange = context?.state?.selectedDateRange || null
    payload.date_range_type = selectedDateRange?.type || null
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .post(`/download`, payload)
        .then((response) => {
          resolve(response?.data || null)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  deleteReportFiles(_context, filePaths) {
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .delete(`/delete`, { data: { file_path: filePaths } })
        .then((response) => {
          resolve(response?.data || null)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  deleteScheduledReport(_context, reportId) {
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .delete(`/delete-schedule-report`, { data: { report_id: reportId } })
        .then((response) => {
          resolve(response?.data || null)
        })
        .catch((error) => {
          reject(error)
        })
    })
  },
  scheduleReport(_context, payload) {
    return new Promise((resolve, reject) => {
      advReportingNoCacheApi
        .post(`/schedule-report`, payload)
        .then((response) => {
          resolve(response?.data || null)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }
}

const mutations = {
  setSchoolList(state, schoolList) {
    state.schoolList = schoolList
  },
  resetScheduledReportsState(state) {
    state.scheduledReports = getScheduledReportsInitialState()
  },
  setScheduledReportsData(state, { total = 0, data = [], current_page = 1 }) {
    state.scheduledReports.scheduledReports = data || []
    state.scheduledReports.pagination.total = Number(total)
    state.scheduledReports.pagination.activePage = Number(current_page) || 1
    state.scheduledReports.pagination.pages = Math.ceil(
      Number(total) / Number(state.scheduledReports.pagination.per_page.value)
    )
  },
  setScheduledReportsSearch(state, search) {
    state.scheduledReports.search = search || ""
    state.scheduledReports.pagination.activePage = 1
  },
  setScheduledReportsPaginationPerPage(state, perPage) {
    perPage.label = perPage?.label || "25 / Page"
    perPage.value = perPage?.value || 25
    state.scheduledReports.pagination.per_page = {
      label: perPage.label,
      value: Number(perPage.value)
    }
    state.scheduledReports.pagination.activePage = 1
  },
  setScheduledReportsPaginationActivePage(state, activePage) {
    state.scheduledReports.pagination.activePage = Number(activePage) || 1
  },
  setScheduledReportsSort(state, sort) {
    state.scheduledReports.sort = sort || null
    state.scheduledReports.pagination.activePage = 1
  },
  setScheduledReportsLoading(state, isLoading) {
    state.scheduledReports.isLoading = isLoading
  },
  setSelectedSchools(state, schools) {
    state.selectedSchools = schools
  },
  setIsAdvanceReportingPageLoading(state, isAdvanceReportingPageLoading) {
    state.isAdvanceReportingPageLoading = isAdvanceReportingPageLoading
  },
  setIsDateDependentChartDataLoading(state, isDateDependentChartDataLoading) {
    state.isDateDependentChartDataLoading = isDateDependentChartDataLoading
  },
  setError(state, error) {
    state.error = error
  },
  setKpiData(state, kpiData) {
    state.kpiData = kpiData
  },
  setSelectedDateRange(state, selectedDateRange) {
    state.selectedDateRange = selectedDateRange
  },
  setDateDependentChartData(state, dateDependentChartData) {
    state.dateDependentChartData = dateDependentChartData
  },
  setDateIndependentChartData(state, dateIndependentChartData) {
    state.dateIndependentChartData = dateIndependentChartData
  },
  setUserPreferences(state, userPreferences) {
    state.userPreferences = userPreferences
  },
  setChartSettings(state, chartSettings) {
    state.chartSettings = chartSettings
  },
  setFrontendIdVsBackendIdMap(state, chartList) {
    const result = {}
    chartList.forEach((chart) => {
      result[advanceReportingConstants.CHART_BE_NAME_VS_ID[chart.name]] =
        chart.id
    })
    state.frontendIdVsBackendIdMap = result
  },
  setRevealedNamesMatrix(state, revealedNamesMatrix) {
    state.revealedNamesMatrix = revealedNamesMatrix || {}
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
